import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";

import { CONTACT_EMAIL } from "../data/constants";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useUserInfo } from "../hooks/useUserInfo";
import { Theme } from "../theme";

// アニメーション
const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	transition: background-color 0.3s ease;
`;

const Container = styled.div`
	max-width: 900px;
	margin: 0 auto;
	padding: 3rem 2rem 4rem 2rem;

	@media (max-width: 768px) {
		padding: 2rem 1rem 3rem 1rem;
	}
`;

const Header = styled.div`
	margin-bottom: 3rem;
	animation: ${fadeInUp} 0.6s ease-out;
`;

const Title = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.pageHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.pageHeading.fontWeight};
	line-height: ${props => props.theme.typography.roles.pageHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.pageHeading.letterSpacing};
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 0.5rem;

	@media (max-width: 768px) {
		letter-spacing: -0.015em;
	}
`;

const LastUpdated = styled.p<{ theme: Theme }>`
	font-size: 0.95rem;
	color: ${props => props.theme.colors.text.tertiary};
	margin-top: 1rem;
`;

const Content = styled.div`
	animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`;

const Intro = styled.p<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	line-height: 1.8;
	margin-bottom: 3rem;
	padding: 1.5rem;
	border-left: 4px solid ${props => props.theme.colors.primary.main};
	background: ${props => props.theme.mode === 'light'
		? props.theme.colors.neutral[100]
		: props.theme.colors.neutral[200]};
	border-radius: 0 8px 8px 0;
`;

const Section = styled.section`
	margin-bottom: 3rem;
`;

const SectionTitle = styled.h2<{ theme: Theme }>`
	font-size: 1.5rem;
	font-weight: 700;
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 1rem;
	padding-bottom: 0.5rem;

	@media (max-width: 768px) {
		font-size: 1.25rem;
	}
`;

const SectionContent = styled.div<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	line-height: 1.8;
	white-space: pre-line;

	a {
		color: ${props => props.theme.colors.primary.main};
		text-decoration: none;
		border-bottom: 1px solid transparent;
		transition: border-color 0.3s ease;
		word-break: break-all;

		&:hover {
			border-bottom-color: ${props => props.theme.colors.primary.main};
		}
	}
`;

const ContactBox = styled.div<{ theme: Theme }>`
	padding: 2rem;
	border-radius: 12px;
	background: ${props => props.theme.mode === 'light'
		? props.theme.colors.neutral[100]
		: props.theme.colors.neutral[200]};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	text-align: center;
`;

const ContactDescription = styled.p<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	margin-bottom: 1.5rem;
	line-height: 1.8;
`;

const EmailLink = styled.a<{ theme: Theme }>`
	display: inline-block;
	color: ${props => props.theme.colors.primary.main};
	font-weight: 600;
	font-size: 1.125rem;
	text-decoration: none;
	padding: 1rem 2rem;
	border-radius: 8px;
	background: ${props => props.theme.mode === 'light'
		? 'rgba(59, 130, 246, 0.1)'
		: 'rgba(59, 130, 246, 0.2)'};
	transition: all 0.3s ease;
	margin-bottom: 1rem;

	&:hover {
		background: ${props => props.theme.mode === 'light'
			? 'rgba(59, 130, 246, 0.2)'
			: 'rgba(59, 130, 246, 0.3)'};
		transform: translateY(-2px);
	}

	@media (max-width: 768px) {
		font-size: 1rem;
		padding: 0.875rem 1.5rem;
	}
`;

const ResponseNote = styled.p<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.tertiary};
	font-size: 0.875rem;
	margin-top: 1rem;
`;

const FooterWrapper = styled.div`
	margin-top: 6rem;
`;

const PrivacyPolicy = () => {
	const { theme } = useTheme();
	const { t } = useLanguage();
	const userInfo = useUserInfo();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const lastUpdatedDate = "2026年8月16日";

	return (
		<React.Fragment>
			<Helmet>
				<title>{`${t.privacyPolicy.title} | ${userInfo.main.title}`}</title>
				<meta name="description" content={t.privacyPolicy.title} />
			</Helmet>

			<PageContainer theme={theme}>
				<NavBar />
				<Container>
					<Header>
						<Title theme={theme}>{t.privacyPolicy.title}</Title>
						<LastUpdated theme={theme}>
							{t.privacyPolicy.lastUpdated}: {lastUpdatedDate}
						</LastUpdated>
					</Header>

					<Content>
						<Intro theme={theme}>{t.privacyPolicy.intro}</Intro>

						<Section>
							<SectionTitle theme={theme}>
								{t.privacyPolicy.sections.collection.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.privacyPolicy.sections.collection.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.privacyPolicy.sections.usage.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.privacyPolicy.sections.usage.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.privacyPolicy.sections.thirdParty.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.privacyPolicy.sections.thirdParty.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.privacyPolicy.sections.disclosure.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.privacyPolicy.sections.disclosure.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.privacyPolicy.sections.security.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.privacyPolicy.sections.security.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.privacyPolicy.sections.rights.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.privacyPolicy.sections.rights.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.privacyPolicy.sections.dataRetention.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.privacyPolicy.sections.dataRetention.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.privacyPolicy.sections.children.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.privacyPolicy.sections.children.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.privacyPolicy.sections.changes.title}
							</SectionTitle>
							<SectionContent theme={theme}>
								{t.privacyPolicy.sections.changes.content}
							</SectionContent>
						</Section>

						<Section>
							<SectionTitle theme={theme}>
								{t.privacyPolicy.sections.contact.title}
							</SectionTitle>
							<ContactBox theme={theme}>
								<ContactDescription theme={theme}>
									{t.privacyPolicy.sections.contact.content}
								</ContactDescription>
								<EmailLink
									href={`mailto:${CONTACT_EMAIL}`}
									theme={theme}
								>
									{CONTACT_EMAIL}
								</EmailLink>
								<ResponseNote theme={theme}>
									{t.privacyPolicy.contactSection.responseNote}
								</ResponseNote>
							</ContactBox>
						</Section>
					</Content>

					<FooterWrapper>
						<Footer />
					</FooterWrapper>
				</Container>
			</PageContainer>
		</React.Fragment>
	);
};

export default PrivacyPolicy;
